import {useEffect, useState} from 'react';
import {Box, DialogContent, Link, Stack, Typography} from '@mui/material';
import {AmountField, BaseDialog, StyledTextField} from '@resurs-internal/mui';
import {
  isRestError,
  tryParseFloat,
  useCountry,
  useCustomer,
  useDepositAccount,
} from '@resurs-internal/rb-app-api';
import {useDepositOpenBankingAccounts} from '@resurs-internal/rb-app-api/features/deposit/hooks/useDepositOpenBankingAccounts/useDepositOpenBankingAccounts';
import {useForm} from 'react-hook-form';
import {useTranslate} from 'hooks';
import {useDialog} from 'hooks/useDialog/useDialog';
import {FontIcon} from '../../../components';
import {createRedirectPath} from '../../../services/uri/createRedirectPath';
import {removeDiacritics} from '../utils/removeDiacritics';
import {DIRECT_TRANSFER_CALLBACK_PATH} from './DepositAccountDirectTransferCallbackPage';

export type DepositAccountDirectTransferDialogState = {accountId: string};

type DirectTransferFormValues = {
  recipientName: string;
  amount: string;
  message: string;
};

const RECIPIENT_NAME_MAX_LENGTH = 35;
const RECIPIENT_NAME_PATTERN =
  /^[A-Za-z\u00C0-\u017F\u0100-\u024F\s'.,:+"/\\€$£₤-]+$/;

export const DepositAccountDirectTransferDialog = () => {
  const translate = useTranslate();
  const country = useCountry();
  const customer = useCustomer();
  const dialog = useDialog<DepositAccountDirectTransferDialogState>(
    'deposit-direct-transfer',
  );
  const accountId = dialog.state?.accountId;
  const account = useDepositAccount(accountId);
  const {initiatePayment} = useDepositOpenBankingAccounts();
  // Our routes carry the internal id, but the payment needs the public one.
  const publicAccountId = account.query.data?.id;

  const [submitError, setSubmitError] = useState<string>();
  const [isRedirecting, setIsRedirecting] = useState(false);

  // Finland issues no BBAN of its own, so the bank does not ask for a recipient.
  const isRecipientNameRequired = country.code !== 'FI';
  const isAccountOwner =
    account.query.data?.currentUserRole === 'ACCOUNT_OWNER';

  const {
    register,
    handleSubmit,
    watch,
    reset,
    setValue,
    formState: {errors, isValid, isSubmitting},
  } = useForm<DirectTransferFormValues>({
    mode: 'all',
    shouldUnregister: true,
    defaultValues: {recipientName: '', amount: '', message: ''},
  });

  const amount = watch('amount');

  useEffect(() => {
    if (!dialog.open) return;
    setSubmitError(undefined);
    reset({recipientName: '', amount: '', message: ''});
  }, [dialog.open, accountId, reset]);

  useEffect(() => {
    if (!dialog.open) return;
    if (!isRecipientNameRequired || !isAccountOwner || !customer.name) return;
    setValue('recipientName', customer.name, {shouldValidate: true});
  }, [
    dialog.open,
    isRecipientNameRequired,
    isAccountOwner,
    customer.name,
    setValue,
  ]);

  const handleClose = () => {
    setSubmitError(undefined);
    reset({recipientName: '', amount: '', message: ''});
    dialog.onClose();
  };

  const handleFormSubmit = async (values: DirectTransferFormValues) => {
    if (!accountId || !publicAccountId) return;

    setSubmitError(undefined);
    const numericAmount = tryParseFloat(values.amount);
    const formattedAmount = country.formatAmount({
      amount: numericAmount,
      currency: country.currency,
    });

    try {
      const response = await initiatePayment.mutateAsync({
        accountId: publicAccountId,
        fundingDetails: {
          amount: {amount: numericAmount, currency: country.currency},
          recipientName: isRecipientNameRequired
            ? removeDiacritics(values.recipientName.trim())
            : undefined,
        },
        transactionText: values.message.trim() || undefined,
        sourceFlow: 'ONLINEBANK',
        context: {
          redirectUrl: createRedirectPath(DIRECT_TRANSFER_CALLBACK_PATH, {
            accountId,
            amount: formattedAmount,
          }),
        },
      });

      const paymentUrl = response._links?.payment?.href;
      if (!paymentUrl) {
        setSubmitError(translate('common.error.subtitle'));
        return;
      }

      setIsRedirecting(true);
      window.location.href = paymentUrl;
    } catch (error) {
      setSubmitError(
        (isRestError(error) && error.message) ||
          translate('common.error.subtitle'),
      );
    }
  };

  const onSubmit = handleSubmit(handleFormSubmit);
  const isBusy = isSubmitting || initiatePayment.isPending || isRedirecting;

  return (
    <BaseDialog
      open={dialog.open}
      onClose={handleClose}
      maxWidth={'sm'}
      header={translate('savings.deposit.direct.title')}
      actions={[
        {
          title: translate('common.cancel'),
          onClick: handleClose,
          disabled: isBusy,
        },
        {
          color: 'primary',
          title: translate('savings.deposit.direct.action'),
          onClick: onSubmit,
          disabled: !isValid || !publicAccountId || isBusy,
        },
      ]}>
      <DialogContent>
        <Stack direction={'column'} spacing={3}>
          <Box>
            {isRecipientNameRequired && (
              <StyledTextField
                outline={'top'}
                fullWidth
                type={'text'}
                label={translate('savings.withdrawal.recipientName')}
                error={Boolean(errors.recipientName)}
                helperText={errors.recipientName?.message}
                {...register('recipientName', {
                  required: true,
                  validate: (value) => {
                    const name = value.trim();
                    if (name.length > RECIPIENT_NAME_MAX_LENGTH) {
                      return translate(
                        'error.savings.withdrawal.recipientName.tooLong',
                      );
                    }
                    if (!RECIPIENT_NAME_PATTERN.test(name)) {
                      return translate(
                        'error.savings.withdrawal.recipientName.invalid',
                      );
                    }
                  },
                })}
              />
            )}
            <AmountField
              outline={isRecipientNameRequired ? 'middle' : 'top'}
              label={translate('savings.deposit.direct.amount')}
              value={amount}
              error={Boolean(errors.amount)}
              helperText={errors.amount?.message}
              formatNumber={(value) =>
                country.formatAmount({
                  amount: value,
                  currency: country.currency,
                })
              }
              {...register('amount', {
                required: true,
                validate: (value) => {
                  if (tryParseFloat(value) <= 0)
                    return translate('common.invalid-input');
                },
              })}
            />
            <StyledTextField
              outline={'bottom'}
              fullWidth
              type={'text'}
              label={translate('savings.deposit.direct.message')}
              error={Boolean(errors.message)}
              helperText={errors.message?.message}
              {...register('message')}
            />
          </Box>

          <Typography variant={'body2'} color={'text.secondary'}>
            {translate('savings.deposit.direct.disclaimer')}
          </Typography>

          <Link
            href={translate('savings.deposit.direct.connect.url')}
            target={'_blank'}
            rel={'noopener noreferrer'}
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}>
            {translate('savings.deposit.direct.connect.link')}
            <FontIcon icon={'arrow-up-right-from-square'} size={'sm'} />
          </Link>

          {submitError && (
            <Typography variant={'body2'} color={'error'}>
              {submitError}
            </Typography>
          )}
        </Stack>
      </DialogContent>
    </BaseDialog>
  );
};
